<script lang="ts">
  // Custom tab bar is used to support per-tab badges and sheet toggles.
  import { Badge } from '@byjackli-labs/ui/Badge'
  import MapPage from './pages/MapPage.svelte'
  import SearchPage from './pages/SearchPage.svelte'
  import FavoritesPage from './pages/FavoritesPage.svelte'
  import AlertsPage from './pages/AlertsPage.svelte'
  import PlanPage from './pages/PlanPage.svelte'
  import Sheet from './components/Sheet.svelte'
  import DevPanel from './components/DevPanel.svelte'
  import FpsIndicator from './components/FpsIndicator.svelte'
  import { Colors } from './ui/colors'
  import { Type } from './ui/theme'
  import { safeBottom, safeBottomButtons, safeLeft, safeRight } from './ui/safeArea'
  import { useTransitStore } from './store/transit'
  import { useTripPlanStore } from './store/tripPlan'
  import { useDevStore } from './store/dev'
  import { storeSelect, storeState } from './lib/store.svelte'
  import { useWebSocket } from './hooks/useWebSocket.svelte'
  import { useBackHandler, useAndroidBackButton } from './hooks/useBackHandler.svelte'
  import { useResizableSheet } from './lib/resizableSheet.svelte'
  import type { Snippet } from 'svelte'

  // Map is a permanent backdrop; tabs toggle bottom sheets.
  type SheetTab = 'plan' | 'search' | 'favorites' | 'alerts'

  const _wh = typeof window !== 'undefined' ? window.innerHeight : 800
  const SHEET_MIN     = 80                          // Minimum visible height
  const SHEET_DEFAULT = Math.round(_wh * 0.50)      // Default sheet height
  const SHEET_MAX     = Math.round(_wh * 0.85)      // Maximum drag height

  // Labels only: the bar marks the current tab with a leading dot and weight.
  const TABS = [
    { key: 'plan'      as const, label: 'Plan' },
    { key: 'search'    as const, label: 'Routes' },
    { key: 'alerts'    as const, label: 'Alerts' },
    { key: 'favorites' as const, label: 'Favorites' },
  ]

  const FAV_TAP_RESET_MS = 700   // Max tap interval for dev menu trigger

  // Exactly one bottom sheet is open at a time; they share the drag-resizable height.
  let openSheet = $state<SheetTab | null>(null)
  const sheet = useResizableSheet({
    minHeight: SHEET_MIN,
    defaultHeight: SHEET_DEFAULT,
    maxHeight: SHEET_MAX,
  })

  // 5 quick taps on Favorites tab opens DevPanel in DEV mode.
  let favTapCount = 0
  let favTapTimer: ReturnType<typeof setTimeout> | null = null

  const dev = storeState(useDevStore)

  // Counts active non-upcoming alerts for tab badge.
  const alertCount = storeSelect(
    useTransitStore,
    (s) => s.alerts.reduce((n, a) => (a.upcoming ? n : n + 1), 0),
  )
  // Indicates whether an active itinerary is selected.
  const tripSelected = storeSelect(useTripPlanStore, (s) => s.activeItinerary !== null)

  useWebSocket()

  // Handles back button navigation and sheet dismissal.
  useBackHandler(() => openSheet !== null, () => { openSheet = null })
  const back = useAndroidBackButton()

  function handleTabPress(key: SheetTab) {
    // Toggle selected sheet and reset height when opening.
    if (openSheet !== key) {
      sheet.reset()
      openSheet = key
    } else {
      openSheet = null
    }

    // Dev panel multi-tap trigger.
    if (key === 'favorites' && import.meta.env.DEV) {
      if (favTapTimer) clearTimeout(favTapTimer)
      favTapCount += 1
      if (favTapCount >= 5) {
        favTapCount = 0
        favTapTimer = null
        useDevStore.getState().setPanelOpen(true)
      } else {
        favTapTimer = setTimeout(() => { favTapCount = 0 }, FAV_TAP_RESET_MS)
      }
    }
  }

  // Header actions snippet passed up from AlertsPage.
  let alertsHeaderActions = $state<Snippet | null>(null)

  // Measured, and published to CSS as `--tab-bar-height`, for the sheets: `--keyboard-inset`
  // is how much of the VIEWPORT's bottom the on-screen keyboard covers, but a sheet is
  // absolutely positioned inside the content area, whose bottom edge stops a tab bar short of
  // it. Without subtracting that, a sheet lifts one tab bar too far and floats with a strip of
  // map showing under it. Measured rather than written down because the bar's height is the
  // nav-bar safe-area inset plus a font's line box, and neither is a constant.
  let tabBarHeight = $state(0)
</script>

<div
  style="
    display: flex; flex-direction: column;
    height: 100dvh; background-color: {Colors.ground};
    font-family: var(--bjl-font-body);
    --tab-bar-height: {tabBarHeight}px;
  "
>
  {#if import.meta.env.DEV}
    <DevPanel />
    {#if dev.value.showFps}
      <FpsIndicator />
    {/if}
  {/if}

  <!-- Page content -->
  <div style="flex: 1; overflow: hidden; position: relative;">

    <!-- Map backdrop -->
    <div style="position: absolute; inset: 0;">
      <MapPage sheetOpen={openSheet !== null} onInteraction={() => { openSheet = null }} />
    </div>

    <Sheet
      title="Plan Trip"
      open={openSheet === 'plan'}
      height={sheet.height}
      dragging={sheet.dragging}
      minHeight={SHEET_MIN}
      onHandleDown={sheet.onHandleDown}
      onClose={() => { openSheet = null }}
      onToggleCollapse={tripSelected.value ? sheet.toggleCollapse : undefined}
    >
      <!-- Only mounted when open to stop geolocation watch when hidden. -->
      {#if openSheet === 'plan'}
        <PlanPage onStartTrip={() => { openSheet = null }} />
      {/if}
    </Sheet>

    <Sheet
      title="Routes"
      open={openSheet === 'search'}
      height={sheet.height}
      dragging={sheet.dragging}
      minHeight={SHEET_MIN}
      onHandleDown={sheet.onHandleDown}
      onClose={() => { openSheet = null }}
    >
      <SearchPage />
    </Sheet>

    <!-- Render header actions only when alerts sheet is open. -->
    <Sheet
      title="Service Alerts"
      open={openSheet === 'alerts'}
      height={sheet.height}
      dragging={sheet.dragging}
      minHeight={SHEET_MIN}
      onHandleDown={sheet.onHandleDown}
      onClose={() => { openSheet = null }}
      headerActions={openSheet === 'alerts' ? alertsHeaderActions ?? undefined : undefined}
    >
      <!-- Only mounted when open to avoid sorting overhead when hidden. -->
      {#if openSheet === 'alerts'}
        <AlertsPage
          onSelectRoutes={() => { openSheet = null }}
          onHeaderActions={(actions) => { alertsHeaderActions = actions }}
        />
      {/if}
    </Sheet>

    <Sheet
      title="Favorites"
      open={openSheet === 'favorites'}
      height={sheet.height}
      dragging={sheet.dragging}
      minHeight={SHEET_MIN}
      onHandleDown={sheet.onHandleDown}
      onClose={() => { openSheet = null }}
    >
      {#if openSheet === 'favorites'}
        <FavoritesPage />
      {/if}
    </Sheet>
  </div>

  <!-- Exit prompt for Android back button -->
  {#if back.exitHint}
    <div
      role="status"
      style="
        position: fixed; bottom: {safeBottom(72)}; left: 50%; transform: translateX(-50%);
        padding: 9px 18px; border-radius: var(--bjl-radius-full, 20px);
        background-color: {Colors.surface};
        border: 1px solid {Colors.borderStrong};
        color: {Colors.textSecondary}; {Type.body}; white-space: nowrap;
        box-shadow: var(--bjl-shadow-md);
        z-index: 400; pointer-events: none;
      "
    >
      Press back again to exit
    </div>
  {/if}

  <!-- Tab bar with safe area padding for navigation bar -->
  <div
    bind:clientHeight={tabBarHeight}
    style="
      display: flex;
      background-color: {Colors.ground};
      border-top: 1px solid {Colors.border};
      padding: 6px {safeRight(8)} {safeBottomButtons()} {safeLeft(8)};
      flex-shrink: 0;
      z-index: 300;
      position: relative;
    "
  >
    <!-- Active tab styling -->
    {#each TABS as { key, label } (key)}
        {@const isActive = openSheet === key}
      <button
        onclick={() => handleTabPress(key)}
        aria-current={isActive ? 'page' : undefined}
        style="
          flex: 1; border: none; background: none; cursor: pointer;
          padding: 14px 0; display: flex;
          align-items: center; justify-content: center; gap: 6px;
          color: {isActive ? Colors.text : Colors.textMuted};
        "
      >
        {#if isActive}
          <!-- Active tab dot -->
          <span style="
            width: 5px; height: 5px; flex-shrink: 0;
            border-radius: 50%; background-color: currentColor;
          "></span>
        {/if}
        <span style="
          font-family: var(--bjl-font-heading, var(--bjl-font-body));
          font-size: 15px; letter-spacing: -0.01em;
          font-weight: {isActive ? 'var(--bjl-font-weight-bold, 700)' : 500};
        ">{label}</span>
        {#if key === 'alerts' && alertCount.value > 0}
          <Badge size="xs" variant="solid" tone="error" shape="pill" register="mono">
            {alertCount.value > 99 ? '99+' : alertCount.value}
          </Badge>
        {/if}
      </button>
    {/each}
  </div>
</div>
